import {Language} from "@/api/data-contracts";
import {LANGUAGE_TO_LNG} from "@/app/locale/languageCodes";

/**
 * Reverse of `LANGUAGE_TO_LNG` (i18next code -> `Language` enum value). Derived from the same
 * source of truth instead of a second hardcoded mapping, so the two never drift apart.
 */
const LNG_TO_LANGUAGE: Record<string, Language> = Object.fromEntries(
    Object.entries(LANGUAGE_TO_LNG).map(([language, lng]) => [lng, language as Language])
);

/**
 * Maps a raw language code (URL `lang` parameter or a `navigator.languages` entry, e.g. `"de"`,
 * `"de-DE"`, `"EN"`) to the matching `Language` enum value. Only the primary subtag before an
 * optional region suffix is considered. Returns `undefined` for a code BoxHub has no `Language`
 * value for (e.g. `"es"`), including a prototype-chain property name (e.g. `"constructor"`) that
 * `LNG_TO_LANGUAGE` never assigned itself — `Object.hasOwn` guards the lookup against that case.
 */
function languageFromCode(code: string): Language | undefined {
    const primarySubtag = code.trim().toLowerCase().split("-")[0];
    return Object.hasOwn(LNG_TO_LANGUAGE, primarySubtag) ? LNG_TO_LANGUAGE[primarySubtag] : undefined;
}

/**
 * Translates the currently active i18next language code (`i18n.language`, e.g. `"de"`, `"en"`,
 * `"de-DE"`) into the `Language` enum value used as a path parameter by the backend endpoints
 * (e.g. `GET /api/legal-documents/{document}/{language}`). Unlike `resolveAnonymousLanguage` this
 * is a pure code translation without an availability check: i18next only ever runs with a code
 * BoxHub knows. A code without a matching `Language` value falls back to `Language.GERMAN`, so
 * callers always get a usable enum value.
 *
 * @param lng - The active i18next language code
 * @returns The matching `Language`, defaulting to `Language.GERMAN`
 */
export function resolveApiLanguage(lng: string | null | undefined): Language {
    return (lng ? languageFromCode(lng) : undefined) ?? Language.GERMAN;
}

/**
 * Resolves the display language for a not-yet-authenticated visitor, following the business rule
 * "language parameter -> the browser's most-preferred maintained language -> English" (see
 * `documentation/sprachdatei-verwaltung.md`), restricted to languages with a maintained/uploaded
 * language file (`availableLanguages`, see `GET /api/i18n/languages`). A parameter without a valid
 * file is ignored entirely rather than falling back to it. The browser language list is walked in
 * priority order (`navigator.languages`); the first entry that maps to a maintained language wins,
 * so a Polish browser lands on Polish once Polish is maintained, exactly as a German browser lands
 * on German. Entries without a maintained file (e.g. Spanish) are skipped; when none matches, the
 * English default applies (or German if English itself is not maintained).
 *
 * @param availableLanguages - Languages with at least one maintained/uploaded language file
 * @param langParam - The `lang` URL query parameter, if present (e.g. `"de"`, `"en"`, `"pl"`)
 * @param navigatorLanguages - The browser's preferred languages, in priority order (`navigator.languages`)
 * @returns The resolved `Language`, defaulting to `Language.ENGLISH` (or `Language.GERMAN` if English is not available)
 */
export function resolveAnonymousLanguage(
    availableLanguages: Language[],
    langParam: string | null | undefined,
    navigatorLanguages: readonly string[]
): Language {
    if (langParam) {
        const fromParam = languageFromCode(langParam);
        if (fromParam !== undefined && availableLanguages.includes(fromParam)) {
            return fromParam;
        }
    }

    for (const navigatorLanguage of navigatorLanguages) {
        const fromBrowser = languageFromCode(navigatorLanguage);
        if (fromBrowser !== undefined && availableLanguages.includes(fromBrowser)) {
            return fromBrowser;
        }
    }

    if (availableLanguages.includes(Language.ENGLISH)) {
        return Language.ENGLISH;
    }

    return Language.GERMAN;
}
